import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { GLASS } from './glass';

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: 'easeOut' },
};

export function HeroSection() {
    const { t } = useLanguage();

    return (
        <section
            id="product"
            className="relative"
            style={{
                paddingTop: 'var(--page-offset, 128px)',
                paddingBottom: '72px',
                scrollMarginTop: 'var(--page-offset, 128px)',
            }}
        >
            <div className="section-container relative z-10 w-full pt-6 lg:pt-0">
                <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
                    {/* Left — statement first */}
                    <div className={`max-w-2xl p-6 sm:p-8 lg:p-10 ${GLASS}`} data-testid="hero-glass-panel">
                        <motion.h1
                            className="font-heading text-foreground text-[2rem] leading-[1.14] sm:text-[2.5rem] sm:leading-[1.08] md:text-[3rem] md:leading-[1.05] lg:text-[3.15rem] lg:leading-[1.04] xl:text-[3.4rem] tracking-[-0.015em] mb-6 md:mb-8 [text-wrap:balance]"
                            {...fadeUp}
                            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.05 }}
                            data-testid="hero-slogan"
                        >
                            <span className="block text-foreground">
                                <span className="lg:hidden">
                                    {t('hero.title_new_line1_a')}<br />{t('hero.title_new_line1_b')}
                                </span>
                                <span className="hidden lg:inline">{t('hero.title_new_line1')}</span>
                            </span>
                            <span className="block text-primary">
                                <span className="lg:hidden">
                                    {t('hero.title_new_line2_a')}<br /><span className="text-secondary-dark">{t('hero.title_new_line2_b')}</span>
                                </span>
                                <span className="hidden lg:inline">
                                    {t('hero.title_new_line2_a')}<br /><span className="text-secondary-dark">{t('hero.title_new_line2_b')}</span>
                                </span>
                            </span>
                        </motion.h1>

                        {/* Body — short and controlled */}
                        <motion.p
                            className="text-base md:text-[17px] text-slate-600 leading-[1.65] mb-8 md:mb-10 max-w-[34ch]"
                            {...fadeUp}
                            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.12 }}
                            data-testid="hero-subtext"
                        >
                            {t('hero.subtext_new')}
                        </motion.p>

                        {/* Text-link CTA — no dominant button, header already carries pre-order */}
                        <motion.div
                            {...fadeUp}
                            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.2 }}
                        >
                            <a
                                href="#ingredients"
                                onClick={(e) => {
                                    e.preventDefault();
                                    const el = document.getElementById('ingredients');
                                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                                className="group inline-flex items-center gap-2 text-sm md:text-[15px] font-semibold text-primary hover:text-primary-dark tracking-wide border-b border-primary/30 hover:border-primary pb-1 transition-colors duration-200"
                                data-testid="hero-explore-link"
                            >
                                <span>{t('hero.explore_cta')}</span>
                                <ArrowRight
                                    size={16}
                                    strokeWidth={2.25}
                                    className="group-hover:translate-x-1 transition-transform duration-200"
                                />
                            </a>
                        </motion.div>
                    </div>

                    {/* Right — product image, breathes */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.98 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                        className="relative flex justify-center items-center"
                    >
                        <motion.img
                            animate={{ y: [0, -8, 0] }}
                            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                            src="/product-mockup-transparent.webp"
                            alt="Sleepstack sleep powder, front of the pouch"
                            className="w-full max-w-[320px] sm:max-w-[380px] lg:max-w-[460px] object-contain mx-auto"
                            data-testid="hero-product-image"
                            fetchPriority="high"
                            width="1122"
                            height="1402"
                        />
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
